import {
  Button,
  Flex,
  useColorModeValue,
  useDisclosure
} from "@chakra-ui/react";
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import { AddIcon } from "@chakra-ui/icons";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ROUTES } from "../../../constants/routes-path";
import { useNavigate } from "react-router-dom";
import ConfirmDeleteDialog from "../../../components/Dialog/confirmDeleteDialog";
import { useCustomToast } from "../../../components/CustomToast/CustomToast";
import { agentScheduleService } from "../../../services/agentScheduleService";
import {
  FaEdit,
  FaTrashAlt,
} from "react-icons/fa";
import SearchTable2 from "../../../components/Tables/SearchTable2";
import { LOCALE_PT_BR } from "../../../constants/date-time";
import Loading from "../../../components/Loading/Loading";
import {
  formatDate,
  getEmptyPageResponse,
  toTablePageableData
} from "../../../utils";

function AgentsSchedulesList() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useState({ orderBy: "data_criacao", orderDirection: "desc"});
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef();
  const [selectedItem, setSelectedItem] = useState(null);
  const toast = useCustomToast();
  const [response, setResponse] = useState([]);

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    onOpen();
  };

  const handleConfirmDelete = async () => {
    try {
      await agentScheduleService.delete(selectedItem.id);

      toast({
        title: t(`settings.agent-schedule.messages.success.delete`),
        status: "success",
      });

      fetchData();
      onClose();
    } catch (error) {
      toast({
        title: t(`settings.agent-schedule.messages.error.delete`),
        status: "error",
      });
      onClose();
    }
  };

  const header = [
    {
      Header: t("settings.agent-schedule.list.table.header.name"),
      accessor: "nome",
    },
    {
      Header: t("settings.agent-schedule.list.table.header.initial-date"),
      accessor: "data_inicial"
    },
    {
      Header: t("settings.agent-schedule.list.table.header.end-date"),
      accessor: "data_final"
    },
    {
      Header: t("settings.agent-schedule.list.table.header.creation-date"),
      accessor: "data_criacao"
    },
    {
      Header: t("settings.agent-schedule.list.table.header.created-by"),
      accessor: "usuario.pessoa.nome",
    },
    {
      Header: t("settings.agent-schedule.list.table.header.actions"),
      disableSortBy: true,
      actions: [
        {
          label: t('settings.agent-schedule.list.table.actions.edit'),
          permission: 'editar-pessoa',
          onClick: (row) => navigate(ROUTES.agentsSchedulesEditPath.replace(":id", row.original.id)),
          icon: <FaEdit />
        },
        {
          label: t('settings.agent-schedule.list.table.actions.delete'),
          permission: 'editar-pessoa',
          onClick: (row) => handleDeleteClick(row.original),
          icon: <FaTrashAlt color="#E53E3E" />,
        }
      ],
    },
  ];

  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchData();
      } catch (error) {
        console.error(t("system.error.api.loading-data"), error);
      }
    };

    void loadData();
  }, []);


  const fetchData = async (params = {}) => {
    params.orderBy = params.orderBy ?? "dataCriacao";
    if (response) setResponse(undefined);

    try {
      params = { ...searchParams, ...params, filters: {nome: params.term} };
      setSearchParams(params);
      const data = await agentScheduleService.list(params);
      setResponse(data);
    } catch (err) {
      setResponse(getEmptyPageResponse(searchParams));
      toast({ title: t(err), status: "error" });
    }
  };

  return (
    <Flex direction="column" pt={{ base: "150px", sm: "125px", lg: "75px" }}>
      <Flex
        w="100%"
        px="30px"
        justify="space-between"
        align="center"
        gap="4"
        direction={{ sm: "column", md: "row" }}
        mb='24px'
      >
        <Button
          variant="list-primary"
          leftIcon={<AddIcon fontSize="sm" />}
          onClick={() => navigate(ROUTES.agentsSchedulesAddPath)}
          alignSelf={{ sm: "flex-start", lg: undefined }}
        >
          {t('settings.agent-schedule.list.buttons.new')}
        </Button>
      </Flex>
      <Card overflowX={{ sm: "scroll", lg: "hidden" }} >
        <ListPageCardHeader title={t("settings.agent-schedule.title")} subtitle={t("settings.agent-schedule.description")} />
        <CardBody>
          {response ? (
            <SearchTable2
                tableData={response.content}
                columnsData={header}
                pageableData={toTablePageableData(response, { defaultField: "date", ...searchParams })}
                onChange={fetchData}
            />
          ) : (
            <Loading />
          )}
        </CardBody>
      </Card>
      <ConfirmDeleteDialog
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleConfirmDelete}
        cancelRef={cancelRef}
        message={t("system.dialogs.delete.message", { entity: t("settings.agent-schedule.entity"), id: selectedItem?.nome })}
      />
    </Flex>
  );
}

export default AgentsSchedulesList;
